import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Step, AuthData, AuthConfig, UserProfile, AccountSelection } from '../../utils/types';

interface AppState {
    currentStep: Step;
    activeTab: 'creation' | 'verification' | 'utility';
    authData: AuthData | null;
    authConfig: AuthConfig;
    faceAuthStatus?: string;
    faceAuthQrUrl?: string;
    otpInput: string;
    transactionId: string;
    xToken: string;
    tToken?: string;  // 👈 New: tToken for communication-mobile selection
    availableAccounts: AccountSelection[];
    selectedAccount: AccountSelection | null;
    abhaSuggestions: string[];
    selectedAbhaAddress: string;
    userProfile: UserProfile | null;
    loginData: { username: string; password: string };
    debugInfo: Record<string, unknown>;
}

type AppAction =
    | { type: 'SET_STEP'; payload: Step }
    | { type: 'SET_ACTIVE_TAB'; payload: 'creation' | 'verification' | 'utility' }
    | { type: 'SET_FACE_AUTH_STATUS'; payload: string }
    | { type: 'SET_FACE_AUTH_QR_URL'; payload: string }
    | { type: 'SET_AUTH_DATA'; payload: AuthData | null }
    | { type: 'SET_AUTH_CONFIG'; payload: AuthConfig }
    | { type: 'SET_OTP_INPUT'; payload: string }
    | { type: 'SET_TRANSACTION_ID'; payload: string }
    | { type: 'SET_X_TOKEN'; payload: string }
    | { type: 'SET_T_TOKEN'; payload: string }  // 👈 New action for tToken
    | { type: 'SET_AVAILABLE_ACCOUNTS'; payload: AccountSelection[] }
    | { type: 'SET_SELECTED_ACCOUNT'; payload: AccountSelection | null }
    | { type: 'SET_ABHA_SUGGESTIONS'; payload: string[] }
    | { type: 'SET_SELECTED_ABHA_ADDRESS'; payload: string }
    | { type: 'SET_USER_PROFILE'; payload: UserProfile | null }
    | { type: 'SET_LOGIN_DATA'; payload: { username: string; password: string } }
    | { type: 'UPDATE_DEBUG_INFO'; payload: Record<string, unknown> }
    | { type: 'RESET_FLOW'; payload?: Partial<AppState> };

const initialState: AppState = {
    currentStep: 'login',
    activeTab: 'creation',
    authData: null,
    authConfig: { type: 'enrollment', method: 'aadhaar', value: '', aadhaarMobileInput: '' },
    otpInput: '',
    transactionId: '',
    xToken: '',
    tToken: undefined,  // 👈 New
    availableAccounts: [],
    selectedAccount: null,
    abhaSuggestions: [],
    selectedAbhaAddress: '',
    userProfile: null,
    loginData: { username: '', password: '' },
    debugInfo: {},
};

const reducer = (state: AppState, action: AppAction): AppState => {
    switch (action.type) {
        case 'SET_STEP':
            return { ...state, currentStep: action.payload };
        case 'SET_ACTIVE_TAB':
            return { ...state, activeTab: action.payload };
        case 'SET_AUTH_DATA':
            return { ...state, authData: action.payload };
        case 'SET_AUTH_CONFIG':
            return { ...state, authConfig: action.payload };
        case 'SET_OTP_INPUT':
            return { ...state, otpInput: action.payload };
        case 'SET_TRANSACTION_ID':
            return { ...state, transactionId: action.payload };
        case 'SET_X_TOKEN':
            return { ...state, xToken: action.payload };
        case 'SET_T_TOKEN':
            return { ...state, tToken: action.payload };
        case 'SET_FACE_AUTH_STATUS':
            return { ...state, faceAuthStatus: action.payload };
        case 'SET_FACE_AUTH_QR_URL':
            return { ...state, faceAuthQrUrl: action.payload };
        case 'SET_AVAILABLE_ACCOUNTS':
            return { ...state, availableAccounts: action.payload };
        case 'SET_SELECTED_ACCOUNT':
            return { ...state, selectedAccount: action.payload };
        case 'SET_ABHA_SUGGESTIONS':
            return { ...state, abhaSuggestions: action.payload };
        case 'SET_SELECTED_ABHA_ADDRESS':
            return { ...state, selectedAbhaAddress: action.payload };
        case 'SET_USER_PROFILE':
            return { ...state, userProfile: action.payload };
        case 'SET_LOGIN_DATA':
            return { ...state, loginData: action.payload };
        case 'UPDATE_DEBUG_INFO':
            return { ...state, debugInfo: { ...state.debugInfo, ...action.payload } };
        case 'RESET_FLOW':
            return {
                ...initialState,
                authData: state.authData,
                currentStep: action.payload?.currentStep || 'authType',
            };
        default:
            return state;
    }
};

const AbdmContext = createContext<{
    state: AppState;
    dispatch: React.Dispatch<AppAction>;
} | undefined>(undefined);

export const AbdmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    return (
        <AbdmContext.Provider value={{ state, dispatch }}>
            {children}
        </AbdmContext.Provider>
    );
};

export const useAbdm = () => {
    const context = useContext(AbdmContext);
    if (!context) throw new Error('useAbdm must be inside AbdmProvider');
    return context;
};